import React, { useEffect, useState, useRef } from 'react';
import Hero from '../components/Hero';
import { Box, Typography, Container } from '@mui/material';
import SectionLayout from '../components/SectionLayout';
import WhoWeAre from '../components/WhoWeAre';
import WhyChooseUs from '../components/WhyChooseUs';
import WhatWeOffer from '../components/WhatWeOffer';
import ExportTeaser from '../components/ExportTeaser';
import WorkTogetherCTA from '../components/WorkTogetherCTA';

const HomePage = () => {
  // Scroll to top on mount, and prevent browser scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
  }, []);
  const [showHighlights, setShowHighlights] = useState(false);
  const highlightsRef = useRef(null);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowHighlights(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (highlightsRef.current) {
      observer.observe(highlightsRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Animation styles
  const animatedStyle = (delay = 0) => ({
    opacity: showHighlights ? 1 : 0,
    transform: showHighlights ? 'translateY(0)' : 'translateY(40px)',
    transition: `opacity 1.1s ${delay}s cubic-bezier(.23,1.01,.32,1), transform 1.1s ${delay}s cubic-bezier(.23,1.01,.32,1)`
  });

  return (
    <>
      <Hero />
      {/* Company Highlights Section with 16:9 aspect ratio */}
      <Box
        ref={highlightsRef}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100vw',
          aspectRatio: { xs: 'unset', sm: '16 / 9' },
          minHeight: { xs: '100vh', sm: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          overflowX: 'hidden',
        }}
      >
        {/* Background Image as <img> */}
        <Box
          component="img"
          src="/banner/banner3.jpg"
          alt="Company Highlights Background"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 1,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.45)',
            zIndex: 2,
          }}
        />
        {/* Centered Content */}
        <Container
          sx={{
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: { xs: '100vh', sm: 'auto' },
            textAlign: 'center',
            color: '#fff',
            py: 0,
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            align="center"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.3rem', sm: '2.1rem', md: '3.2rem' },
              mb: 4,
              color: '#fff',
              letterSpacing: '0.03em',
              textShadow: '0 4px 24px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.7)',
              ...animatedStyle(0),
            }}
          >
            Company Highlights
          </Typography>
          <SectionLayout
            left={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1.5, sm: 3 },
                  maxWidth: { xs: 340, sm: 'none' },
                  mx: { xs: 'auto', sm: 0 },
                }}
              >
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 3, px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 2 }, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.08)', ...animatedStyle(0.2) }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1.05rem', sm: '1.4rem', md: '1.9rem' }, mb: { xs: 0.5, sm: 1 }, textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
                    High-quality Veneer & Plywood
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#fff', fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.25rem' }, fontWeight: 500, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                    Only the best, FSC-certified timber products.
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 3, px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 2 }, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.08)', ...animatedStyle(0.4) }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1.05rem', sm: '1.4rem', md: '1.9rem' }, mb: { xs: 0.5, sm: 1 }, textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
                    Global Export Expertise
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#fff', fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.25rem' }, fontWeight: 500, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                    Decades of experience shipping worldwide.
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 3, px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 2 }, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.08)', ...animatedStyle(0.6) }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1.05rem', sm: '1.4rem', md: '1.9rem' }, mb: { xs: 0.5, sm: 1 }, textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
                    Sustainable Sourcing
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#fff', fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.25rem' }, fontWeight: 500, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                    Responsible forestry and eco-friendly practices.
                  </Typography>
                </Box>
              </Box>
            }
            right={
              <Box
                sx={{
                  minHeight: 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...animatedStyle(0.8),
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.85)',
                    p: 2,
                    borderRadius: 4,
                    boxShadow: '0 2px 12px 0 rgba(0,0,0,0.10)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src="/digiwood_logo-removebg-preview.png"
                    alt="Digi Wood Logo"
                    style={{ maxWidth: '90%', maxHeight: 140, objectFit: 'contain', display: 'block' }}
                  />
                </Box>
              </Box>
            }
          />
        </Container>
      </Box>
      <WhoWeAre />
      <WhyChooseUs />
      <WhatWeOffer />
      <ExportTeaser />
      <WorkTogetherCTA />
    </>
  );
};

export default HomePage; 